import React, { useState, useEffect, useRef } from 'react';
import { Button, Box, Tooltip, Typography, CircularProgress, Alert, IconButton, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Card, CardContent } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import axios from '../axios';
import PresenzeDinoAlunniDetail from './PresenzeDinoAlunniDetail';
import dayjs from 'dayjs';
import 'dayjs/locale/it';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';

const MySwal = withReactContent(Swal);

const CorsoLezioni = ({ courseId }) => {
    const [lezioni, setLezioni] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [openModal, setOpenModal] = useState(false);
    const [selectedLezione, setSelectedLezione] = useState(null);
    const [openDialog, setOpenDialog] = useState(false);
    const [roadmap, setRoadmap] = useState([]);
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [snackbarMessage, setSnackbarMessage] = useState('');
    const [snackbarSeverity, setSnackbarSeverity] = useState('success');

    const detailRef = useRef();
    const [isDetailDirty, setIsDetailDirty] = useState(false);

    useEffect(() => {
        fetchLezioni();
        fetchRoadmap();
    }, [courseId]);

    const fetchLezioni = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.post('/datahandler/get-corso-lezioni', {
                token,
                idCorso: courseId
            });
            setLezioni(response.data);
        } catch (error) {
            setError('Errore nel recupero delle lezioni');
        } finally {
            setLoading(false);
        }
    };

    const fetchRoadmap = async () => {
        const token = localStorage.getItem('token');
        try {
            const response = await axios.post('datahandler/corso-alunno-roadmap', {
                idCorso: courseId,
                token: token,
            });
            setRoadmap(response.data);
        } catch (error) {
            console.error('Error fetching roadmap:', error);
        }
    };

    const handleUnlockPresenze = async () => {
        try {
            const token = localStorage.getItem('token');
            await axios.post('/datahandler/unlock-presenze', {
                token,
                idCorso: courseId
            });
            setOpenDialog(false);
            fetchLezioni();
        } catch (error) {
            console.error('Errore nello sblocco delle presenze:', error);
        }
    };

    const showSnackbar = (message, severity = 'success') => {
        setSnackbarMessage(message);
        setSnackbarSeverity(severity);
        setOpenSnackbar(true);
    };

    const sendViaggio = async (data, codice, updateCourse) => {
        const token = localStorage.getItem('token');
        try {
            await axios.post('datahandler/send-viaggio', { ...data, token });
            showSnackbar(`${codice} inviato con successo`, 'success');
            if (updateCourse) {
                fetchRoadmap();
            }
        } catch (error) {
            console.error('Error sending viaggio:', error);
            showSnackbar('Errore durante l\'invio del viaggio', 'error');
            Swal.fire('Errore', 'Errore durante l\'invio del viaggio', 'error');
        }
    };

    const getChipProps = (item) => {
        const currentDate = new Date();
        const phaseDate = new Date(
            parseInt(item.datafase.substring(0, 4)),
            parseInt(item.datafase.substring(4, 6)) - 1,
            parseInt(item.datafase.substring(6, 8))
        );

        if (item.sent > 0) {
            return { backgroundColor: '#4CAF50', color: 'white', tooltip: 'Messaggio inviato' };
        } else if (phaseDate < currentDate) {
            return { backgroundColor: '#ff0000', color: 'white', tooltip: 'Messaggio da inviare, data trascorsa' };
        } else {
            return { backgroundColor: '#B0BEC5', color: 'white', tooltip: 'Messaggio non inviato' };
        }
    };

    const handleChipClick = async (item) => {
        const token = localStorage.getItem('token');
        let anteprima = 'Caricamento anteprima...';

        try {
            const response = await axios.post('/datahandler/anteprima-messaggio', {
                idTemplate: item.id,
                token
            });
            anteprima = response.data.testo || 'Nessuna anteprima disponibile.';
        } catch (error) {
            console.error('Errore nel recupero anteprima:', error);
            anteprima = 'Errore nel caricamento dell\'anteprima.';
        }

        const result = await MySwal.fire({
            title: `<span style="font-weight: 800; color: #1a73e8; font-size: 1.4rem;">Invia ${item.codice}</span>`,
            html: `
                <div style="text-align: left; padding: 10px;">
                    <p style="margin-bottom: 15px; color: #555; font-weight: 600;">Vuoi inviare questo viaggio a tutti i Dinoalunni del corso?</p>
                    <div style="
                        position: relative;
                        margin-top: 10px;
                        background: #fff;
                        border-radius: 12px;
                        padding: 20px;
                        border: 1px solid #e0e0e0;
                        box-shadow: 0 4px 15px rgba(0,0,0,0.05);
                        background-image: radial-gradient(#e0e0e0 0.5px, transparent 0.5px);
                        background-size: 20px 20px;
                    ">
                        <div style="
                            position: absolute;
                            top: -10px;
                            left: 20px;
                            background: #1a73e8;
                            color: white;
                            padding: 2px 12px;
                            border-radius: 20px;
                            font-size: 0.75rem;
                            font-weight: 700;
                            box-shadow: 0 2px 5px rgba(26,115,232,0.3);
                        ">ANTREPRIMA MESSAGGIO</div>
                        <div style="
                            white-space: pre-wrap;
                            max-height: 250px;
                            overflow-y: auto;
                            color: #333;
                            font-family: 'Inter', sans-serif;
                            font-size: 0.95rem;
                            line-height: 1.6;
                            padding-top: 5px;
                        ">
                            ${anteprima}
                        </div>
                    </div>
                </div>
            `,
            showCancelButton: true,
            confirmButtonText: `<span style="display: flex; align-items: center; gap: 8px;"><i class="fas fa-paper-plane"></i> Invia ora!</span>`,
            cancelButtonText: "Annulla",
            showCloseButton: true,
            focusConfirm: false,
            customClass: {
                popup: 'glass-popup-swal',
                confirmButton: 'btn-3d btn-3d-dark-orange btn-3d-lg',
                cancelButton: 'btn-3d btn-3d-grey btn-3d-lg'
            },
            buttonsStyling: false,
            width: '500px'
        });

        if (result.isConfirmed) {
            await sendViaggio({ idAlunno: 0, idCorso: courseId, idTemplate: item.id }, item.codice, true);
        }
    };


    const handleOpenModal = (lezione) => {
        setSelectedLezione(lezione);
        setOpenModal(true);
    };

    const handleCloseModal = async (event, reason, force = false) => {
        // If dirty and not forcing the close (force is true after successful save)
        if (isDetailDirty && !force) {
            const result = await MySwal.fire({
                title: 'Cambiamenti non salvati',
                text: 'Hai dei cambiamenti non salvati, cosa vuoi fare?',
                icon: 'warning',
                showDenyButton: true,
                showCancelButton: true,
                confirmButtonText: 'Salva',
                denyButtonText: 'Scarta i cambiamenti',
                cancelButtonText: 'Resta qui',
                confirmButtonColor: '#ff9800',
                denyButtonColor: '#999',
            });

            if (result.isConfirmed) {
                if (detailRef.current) {
                    await detailRef.current.save();
                }
                return;
            } else if (result.isDenied) {
                // Procedi con la chiusura
            } else {
                return;
            }
        }

        setOpenModal(false);
        setSelectedLezione(null);
        setIsDetailDirty(false);
        fetchLezioni();
    };


    const handleOpenDialog = () => setOpenDialog(true);
    const handleCloseDialog = () => setOpenDialog(false);

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" height="200px">
                <CircularProgress />
            </Box>
        );
    }

    if (error) {
        return <Alert severity="error">{error}</Alert>;
    }

    return (
        <Box sx={{ mt: 2 }}>
            {/* Roadmap Section */}
            <Box sx={{ mb: 4 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#555', mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 4, height: 16, bgcolor: '#0288d1', borderRadius: 4 }} />
                    Roadmap Viaggi
                </Typography>
                <Box sx={{
                    display: 'flex',
                    flexWrap: 'wrap',
                    gap: 1.5,
                    p: 2,
                    background: 'rgba(255, 255, 255, 0.3)',
                    borderRadius: '16px',
                    border: '1px solid rgba(255, 255, 255, 0.5)',
                    backdropFilter: 'blur(10px)',
                    boxShadow: 'inset 0 1px 1px rgba(255,255,255,0.4)',
                    justifyContent: 'center'
                }}>
                    {roadmap.length > 0 ? (
                        roadmap.map(item => {
                            const { backgroundColor } = getChipProps(item);
                            let btnClass = "btn-3d ";
                            if (backgroundColor === '#4CAF50') btnClass += "btn-3d-green";
                            else if (backgroundColor === '#ff0000') btnClass += "btn-3d-red";
                            else btnClass += "btn-3d-grey";

                            return (
                                <Tooltip key={item.id} title={getChipProps(item).tooltip} arrow placement="top">
                                    <Button
                                        variant="contained"
                                        className={btnClass}
                                        onClick={() => handleChipClick(item)}
                                        sx={{
                                            fontSize: '0.75rem',
                                            fontWeight: 700,
                                            minWidth: '100px',
                                            height: 36,
                                            textTransform: 'none'
                                        }}
                                    >
                                        {item.codice}
                                    </Button>
                                </Tooltip>
                            );
                        })
                    ) : (
                        <Typography variant="caption" sx={{ color: '#999', fontStyle: 'italic' }}>
                            Nessuna roadmap disponibile.
                        </Typography>
                    )}
                </Box>
            </Box>

            {/* Attendance Registry Section */}
            <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#555', mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 4, height: 16, bgcolor: '#ff9800', borderRadius: 4 }} />
                    Registro Presenze
                </Typography>

                {lezioni.length === 0 ? (
                    <Card sx={{
                        background: 'rgba(255, 255, 255, 0.3)',
                        backdropFilter: 'blur(10px)',
                        borderRadius: '16px',
                        border: '1px dashed rgba(0,0,0,0.1)',
                        boxShadow: 'none'
                    }}>
                        <CardContent>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 600 }}>
                                    Nessuna lezione trovata o corso non extrascolastico.
                                </Typography>
                                <Button
                                    variant="contained"
                                    className="btn-3d btn-3d-orange"
                                    onClick={handleOpenDialog}
                                    size="small"
                                >
                                    Gestisci registro presenze
                                </Button>
                            </Box>
                        </CardContent>
                    </Card>
                ) : (
                    <Box sx={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 1.5,
                        p: 2,
                        background: 'rgba(255, 255, 255, 0.3)',
                        borderRadius: '16px',
                        border: '1px solid rgba(255, 255, 255, 0.5)',
                        backdropFilter: 'blur(10px)',
                        boxShadow: 'inset 0 1px 1px rgba(255,255,255,0.4)',
                        justifyContent: 'center'
                    }}>
                        {renderLezioni(lezioni, handleOpenModal)}
                    </Box>
                )}
            </Box>

            {/* Modal for attendance detail */}
            <Dialog
                open={openModal}
                onClose={handleCloseModal}
                fullWidth
                maxWidth="sm"
                disableEscapeKeyDown={isDetailDirty}
                PaperProps={{
                    sx: {
                        borderRadius: '24px',
                        background: 'rgba(255, 255, 255, 0.9)',
                        backdropFilter: 'blur(20px)',
                        border: '1px solid rgba(255,255,255,0.4)',
                        overflow: 'hidden'
                    }
                }}
            >
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', p: 2.5, background: 'rgba(0,0,0,0.02)' }}>
                    <Typography variant="h6" sx={{ fontWeight: 800 }}>Dettaglio Lezione</Typography>
                    <IconButton size="small" onClick={handleCloseModal}><CloseIcon /></IconButton>
                </DialogTitle>
                <DialogContent sx={{ p: 4 }}>
                    {selectedLezione && (
                        <PresenzeDinoAlunniDetail
                            ref={detailRef}
                            idCorso={courseId}
                            idLezione={selectedLezione.id}
                            numLezione={selectedLezione.progr_lezione}
                            statoLezione={selectedLezione.status}
                            dataLezione={selectedLezione.data}
                            onSaveSuccess={fetchLezioni}
                            onCancel={handleCloseModal}
                            onDirtyChange={setIsDetailDirty}
                        />
                    )}
                </DialogContent>
            </Dialog>

            {/* Activation Dialog */}
            <Dialog
                open={openDialog}
                onClose={handleCloseDialog}
                PaperProps={{ sx: { borderRadius: '20px' } }}
            >
                <DialogTitle sx={{ fontWeight: 800 }}>Gestire il registro presenze?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        Questa azione permetterà la gestione del registro presenze per questo corso.
                    </DialogContentText>
                </DialogContent>
                <DialogActions sx={{ p: 2.5 }}>
                    <Button onClick={handleCloseDialog} color="inherit" sx={{ fontWeight: 700 }}>Annulla</Button>
                    <Button onClick={handleUnlockPresenze} variant="contained" className="btn-3d btn-3d-primary">Conferma</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

const renderLezioni = (lezioni, handleOpenModal) => {
    const today = dayjs();
    const lezioniAttive = lezioni.filter(lezione => lezione.status === "1");
    let daysFromLastLezione = 0;
    let lastLezioneIndex = -1;

    if (lezioniAttive.length > 0) {
        const lastLezione = lezioniAttive[lezioniAttive.length - 1];
        const lastLezioneDate = dayjs(lastLezione.data);
        daysFromLastLezione = today.diff(lastLezioneDate, 'day');
        lastLezioneIndex = lezioni.findIndex(lezione => lezione.id === lastLezione.id);
    }

    const numLezioniRosse = Math.floor(daysFromLastLezione / 7);

    return lezioni.map((lezione, index) => {
        const isMissing = index > lastLezioneIndex && index <= lastLezioneIndex + numLezioniRosse;
        let btnClass = "btn-3d ";

        if (lezione.status === "1") btnClass += "btn-3d-green";
        else if (isMissing) btnClass += "btn-3d-red";
        else btnClass += "btn-3d-grey";

        return (
            <Tooltip title={isMissing ? "Presenze non registrate" : ""} key={lezione.id} arrow placement="top">
                <Button
                    variant="contained"
                    className={btnClass}
                    sx={{
                        margin: '5px',
                        fontSize: '0.75rem',
                        fontWeight: 700,
                        width: '120px',
                        height: '44px',
                        position: 'relative',
                        textTransform: 'none',
                        lineHeight: 1.2,
                        px: 1
                    }}
                    onClick={() => handleOpenModal(lezione)}
                >
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <Typography variant="inherit" sx={{ fontWeight: 800 }}>
                            Lezione {lezione.progr_lezione}
                        </Typography>
                        {lezione.data && (
                            <Typography variant="caption" sx={{ fontSize: '0.65rem', opacity: 0.9 }}>
                                {dayjs(lezione.data).format('DD/MM/YYYY')}
                            </Typography>
                        )}
                    </Box>
                    {isMissing && (
                        <span style={{
                            position: 'absolute',
                            top: -2,
                            right: 4,
                            color: '#FFEB3B',
                            fontSize: '1.4rem',
                            fontWeight: 900,
                            textShadow: '0 1px 2px rgba(0,0,0,0.5)'
                        }}>!</span>
                    )}
                </Button>
            </Tooltip>
        );
    });
};

export default CorsoLezioni;
